/**
 * useWebSocket Hook
 * AIDLC Administrative Backend
 *
 * Socket.io client connection for real-time agent status updates.
 * Architecture Document Section 04 defines these WebSocket events:
 *
 * Server -> Client:
 *   agent:status    - Agent status changed (from bridge or API)
 *   agent:output    - Agent produced output
 *   prompt:status   - Prompt queue item status changed
 *   session:start   - New agent session started
 *   session:end     - Agent session ended
 *   audit:entry     - New audit log entry created
 *   bridge:ready    - Bridge file watcher is active
 *   bridge:status   - Bridge status response
 *   bridge:activity - Bridge detected file change
 *
 * Client -> Server:
 *   subscribe:agent       - Subscribe to specific agent updates
 *   unsubscribe:agent     - Unsubscribe from agent updates
 *   bridge:status:request - Request current bridge status
 *
 * Security: Token is sent via auth handshake, not in query params (per Bill).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

const WS_URL = import.meta.env.VITE_WS_URL || '';

export type WebSocketEvent =
  | 'agent:status'
  | 'agent:output'
  | 'prompt:status'
  | 'session:start'
  | 'session:end'
  | 'audit:entry'
  | 'bridge:ready'
  | 'bridge:status'
  | 'bridge:activity';

interface UseWebSocketOptions {
  /** Access token for authentication */
  token?: string;
  /** Auto-connect on mount (default: true) */
  autoConnect?: boolean;
  /** Reconnection attempts (default: 5) */
  reconnectionAttempts?: number;
}

interface UseWebSocketResult {
  isConnected: boolean;
  error: string | null;
  socket: Socket | null;
  subscribe: (event: WebSocketEvent, handler: (data: unknown) => void) => void;
  unsubscribe: (event: WebSocketEvent, handler: (data: unknown) => void) => void;
  subscribeToAgent: (agentId: string) => void;
  unsubscribeFromAgent: (agentId: string) => void;
  disconnect: () => void;
  reconnect: () => void;
}

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketResult {
  const {
    token,
    autoConnect = true,
    reconnectionAttempts = 5,
  } = options;

  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(() => {
    if (!WS_URL) {
      console.warn('[WebSocket] VITE_WS_URL not configured. Real-time updates disabled.');
      return;
    }

    if (socketRef.current?.connected) return;

    const socket = io(WS_URL, {
      auth: token ? { token } : undefined,
      transports: ['websocket', 'polling'],
      reconnectionAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    socket.on('connect', () => {
      setIsConnected(true);
      setError(null);
      console.info('[WebSocket] Connected');
    });

    socket.on('disconnect', (reason) => {
      setIsConnected(false);
      console.info('[WebSocket] Disconnected:', reason);
    });

    socket.on('connect_error', (err) => {
      setError(err.message);
      console.error('[WebSocket] Connection error:', err.message);
    });

    socketRef.current = socket;
  }, [token, reconnectionAttempts]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  const subscribe = useCallback(
    (event: WebSocketEvent, handler: (data: unknown) => void) => {
      socketRef.current?.on(event, handler);
    },
    []
  );

  const unsubscribe = useCallback(
    (event: WebSocketEvent, handler: (data: unknown) => void) => {
      socketRef.current?.off(event, handler);
    },
    []
  );

  const subscribeToAgent = useCallback((agentId: string) => {
    socketRef.current?.emit('subscribe:agent', { agentId });
  }, []);

  const unsubscribeFromAgent = useCallback((agentId: string) => {
    socketRef.current?.emit('unsubscribe:agent', { agentId });
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    connect();
  }, [disconnect, connect]);

  useEffect(() => {
    if (autoConnect) {
      connect();
    }
    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  return {
    isConnected,
    error,
    socket: socketRef.current,
    subscribe,
    unsubscribe,
    subscribeToAgent,
    unsubscribeFromAgent,
    disconnect,
    reconnect,
  };
}
